import { get, post } from "../index";

// The merged project timeline: one row per push, carrying BOTH diff
// lanes — geometry (Commit → CommitDiff: fragment/param hashes, tiles)
// and estimation (Diff: components, materials, pricing) — joined on
// Diff.commitId.
//
// Either facet can be null and callers must handle that: the estimation
// lane is computed asynchronously, and versions created in the web app
// mint no commit at all. See GET /commits/timeline/:projectId.
export const getProjectTimeline = (projectId, { limit, from, to } = {}) => {
  const p = new URLSearchParams();
  if (limit) p.append("limit", limit);
  if (from) p.append("from", from);
  if (to) p.append("to", to);
  const qs = p.toString();
  return get(`/commits/timeline/${projectId}${qs ? `?${qs}` : ""}`);
};

// A large diff (SpaceX: 1.43M elements a side) takes minutes to compute
// the first time; the server answers {computing: true} rather than holding
// the request past a proxy timeout. Ask again until it is ready - or
// give up after `maxWaitMs` and hand back that last answer.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
export async function untilComputed(fetchOnce, { maxWaitMs = 10 * 60 * 1000 } = {}) {
  const started = Date.now();
  for (;;) {
    const body = await fetchOnce();
    if (!body || !body.computing) return body;
    if (Date.now() - started > maxWaitMs) return body;
    await sleep(Math.min(Math.max(Number(body.retryAfterMs) || 10000, 2000), 30000));
  }
}

export const getCommitDiff = (commitId, { limit, maxWaitMs } = {}) =>
  untilComputed(() => get(`/commits/${commitId}/diff${limit ? `?limit=${limit}` : ""}`), { maxWaitMs });

// The geometry behind a diff, both generations of it - what the "Show
// model edits" drawer draws. See back/routes/commits.js.
//
// `context` / `contextScope` ask for the UNCHANGED model as well, to
// ghost the change against. Scope "model" is everything else in the
// export; the server's own default ("tiles") ghosts only the change's
// immediate neighbours, which on a one-element push is a couple of dozen
// out of a thousand and looks like the ghost is simply not working.
export const getCommitEditFragments = (commitId, { limit, context, contextScope } = {}) => {
  const p = new URLSearchParams();
  if (limit) p.append("limit", limit);
  if (context !== undefined) p.append("context", context);
  if (contextScope) p.append("contextScope", contextScope);
  const qs = p.toString();
  return untilComputed(() => get(`/commits/${commitId}/edit-fragments${qs ? `?${qs}` : ""}`));
};

export const getElementHistory = (projectId, uniqueId) =>
  get(`/commits/element-history/${projectId}/${encodeURIComponent(uniqueId)}`);

// ─── Revision approval (Review Center) ───────────────────────────────────
//
// Model pushes on a project with requireCommitApproval land 'pending':
// stored and inspectable, but not merged into the project's history -
// not a baseline for the next diff, no element history - until someone
// approves them here.
export const getRevisionQueue = ({ projectId, limit } = {}) => {
  const p = new URLSearchParams();
  if (projectId) p.append("projectId", projectId);
  if (limit) p.append("limit", limit);
  const qs = p.toString();
  return get(`/commits/review-queue${qs ? `?${qs}` : ""}`);
};

export const approveRevision = (commitId, { note } = {}) =>
  post(`/commits/${commitId}/approve`, { note: note || "" });

export const rejectRevision = (commitId, { note } = {}) =>
  post(`/commits/${commitId}/reject`, { note: note || "" });

// ─── Model tree (the 3D canvas's browsable index) ────────────────────────
//
// Categories and types with counts; pass a category (and optionally a
// type) for its elements, or mode:"uids" for just the ids - which is
// what "hide this whole trade" needs. Built server-side from the
// export's manifest + tile sidecars, see back/utils/modelTree.js.
export const getModelTree = (projectId, { manifestPath, category, type, limit, mode, diffCommitId } = {}) => {
  const p = new URLSearchParams();
  if (manifestPath) p.append("manifestPath", manifestPath);
  if (category) p.append("category", category);
  if (type) p.append("type", type);
  if (limit) p.append("limit", limit);
  if (mode) p.append("mode", mode);
  // Annotates every node and element with what THIS push did to it.
  if (diffCommitId) p.append("diffCommitId", diffCommitId);
  const qs = p.toString();
  return get(`/commits/model-tree/${projectId}${qs ? `?${qs}` : ""}`);
};
